import React, { useState, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { X, Upload, Globe, Building, Trash2 } from 'lucide-react';

const LANGUAGES = [
  { code: 'en', name: 'English', flag: '🇺🇸' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'pt', name: 'Português', flag: '🇧🇷' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'ko', name: '한국어', flag: '🇰🇷' },
  { code: 'zh', name: '中文', flag: '🇨🇳' },
];

export default function SettingsModal({ onClose }) {
  const { user, updateProfile, uploadLogo, language, setLanguage } = useAuth();
  const [churchName, setChurchName] = useState(user?.church_name || '');
  const [selectedLang, setSelectedLang] = useState(language);
  const [saving, setSaving] = useState(false);
  const [logoPreview, setLogoPreview] = useState(user?.logo_url || null);
  const fileInputRef = useRef(null);

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateProfile({ 
        church_name: churchName,
        language: selectedLang 
      });
      setLanguage(selectedLang);
      onClose();
    } catch (err) {
      console.error('Failed to save settings:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleLogoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Preview
    const reader = new FileReader();
    reader.onload = (e) => setLogoPreview(e.target.result);
    reader.readAsDataURL(file);
    
    // Upload
    try {
      await uploadLogo(file);
    } catch (err) {
      console.error('Failed to upload logo:', err);
      alert('Failed to upload logo. Please try a smaller image (max 2MB).');
    }
  };

  const removeLogo = async () => {
    setLogoPreview(null);
    // In a full implementation, you'd call an API to remove the logo
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="settings-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
        <div className="p-6 border-b border-[#E2DFD8]">
          <div className="flex items-center justify-between">
            <h2 className="font-heading text-2xl font-bold text-[#1B3A6B]">Settings</h2>
            <button onClick={onClose} className="p-2 hover:bg-[#F4F2EC] rounded-lg">
              <X className="w-5 h-5 text-[#475569]" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Church Logo */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-[#0F1A2A] mb-3">
              <Building className="w-4 h-4" />
              Church Logo
            </label>
            <div className="flex items-center gap-4">
              {logoPreview ? (
                <div className="relative">
                  <img 
                    src={logoPreview} 
                    alt="Church logo" 
                    className="w-20 h-20 object-contain rounded-lg border border-[#E2DFD8]"
                  />
                  <button
                    onClick={removeLogo}
                    className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              ) : (
                <div className="w-20 h-20 bg-[#F4F2EC] rounded-lg flex items-center justify-center border-2 border-dashed border-[#E2DFD8]">
                  <Building className="w-8 h-8 text-[#475569]" />
                </div>
              )}
              <div>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleLogoChange}
                  accept="image/*"
                  className="hidden"
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="btn-secondary px-4 py-2 rounded-lg text-sm flex items-center gap-2"
                  data-testid="upload-logo-button"
                >
                  <Upload className="w-4 h-4" />
                  Upload Logo
                </button>
                <p className="text-xs text-[#475569] mt-2">Max 2MB. PNG, JPG, or SVG.</p>
              </div>
            </div>
          </div>

          {/* Church Name */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-[#0F1A2A] mb-2">
              <Building className="w-4 h-4" />
              Church/Ministry Name
            </label>
            <input
              type="text"
              value={churchName}
              onChange={(e) => setChurchName(e.target.value)}
              placeholder="Enter your church or ministry name"
              className="w-full px-4 py-3 bg-[#F9F8F6] rounded-lg border-none focus:ring-2 focus:ring-[#1B3A6B]"
              data-testid="church-name-input"
            />
            <p className="text-xs text-[#475569] mt-2">This will appear on shared studies and exports.</p>
          </div>

          {/* Language */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-[#0F1A2A] mb-2">
              <Globe className="w-4 h-4" />
              Interface Language
            </label>
            <div className="grid grid-cols-2 gap-2">
              {LANGUAGES.map(lang => (
                <button
                  key={lang.code}
                  onClick={() => setSelectedLang(lang.code)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg border-2 transition-colors ${
                    selectedLang === lang.code
                      ? 'border-[#1B3A6B] bg-[#F9F8F6]'
                      : 'border-[#E2DFD8] hover:border-[#B8860B]/50'
                  }`}
                  data-testid={`language-${lang.code}`}
                >
                  <span className="text-xl">{lang.flag}</span>
                  <span className="font-medium text-[#0F1A2A]">{lang.name}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="p-6 border-t border-[#E2DFD8] flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 btn-secondary py-3 rounded-lg font-medium"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex-1 btn-primary py-3 rounded-lg font-medium"
            data-testid="save-settings-button"
          >
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </div>
    </div>
  );
}
